import {
  formatDuration,
  formatDurationAccessible,
} from '@woolworthslimited/core-components-react/shared/lib/time-utils';
import clsx from 'clsx';

import styles from './recipe.module.scss';
import { FeatureRecipeTileProps } from './recipe.types';
import FeatureRecipeSaveButton from './save-button/save-button';

const FeatureRecipeTile = (props: FeatureRecipeTileProps) => {
  const {
    ref,
    recipeId,
    variation = 'vertical',
    hasBorder = true,
    title,
    image,
    preparation,
    cook,
    difficulty,
    serve,
    source,
    isSponsored,
    isSaved = false,
    url,
    clickHandler,
    saveClickHandler,
    removeClickHandler,
  } = props;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    clickHandler?.();
  };

  const parts = [
    preparation?.time &&
      `${preparation.label ?? 'Preparation time:'} ${formatDurationAccessible(preparation.time)}`,
    cook?.time &&
      `${cook.label ?? 'Cooking time:'} ${formatDurationAccessible(cook.time)}`,
    serve?.total && `${serve.label ?? 'Serves:'} ${serve.total}`,
    difficulty?.level &&
      `${difficulty.label ?? 'Difficulty:'} ${difficulty.level}`,
    isSponsored && 'Sponsored',
  ];

  const ariaLabel = [title, ...parts].filter(Boolean).join('. ');

  return (
    <div
      ref={ref}
      className={clsx(styles['recipe-tile'], styles[`${variation}`])}
    >
      <a
        href={url}
        className={clsx(
          styles['recipe-tile-link'],
          hasBorder && [styles['border']]
        )}
        onClick={handleClick}
        aria-label={ariaLabel}
      >
        <div className={styles['recipe-tile-image']}>
          <img loading="lazy" src={image} alt={title} aria-hidden="true" />
        </div>

        <div className={styles['recipe-tile-content']} aria-hidden="true">
          <h3 className={styles['title']}>{title}</h3>

          {isSponsored && <div className={styles['sponsored']}>Sponsored</div>}

          {(preparation?.time || cook?.time || difficulty?.level) && (
            <div className={styles['details']}>
              {preparation && (
                <div>
                  <div className={styles['detail-label']}>
                    {preparation.label ?? 'Prep'}
                  </div>
                  <div className={styles['detail-info']}>
                    {formatDuration(preparation.time)}
                  </div>
                </div>
              )}
              {cook && (
                <div>
                  <div className={styles['detail-label']}>
                    {cook.label ?? 'Cook'}
                  </div>
                  <div className={styles['detail-info']}>
                    {formatDuration(cook.time)}
                  </div>
                </div>
              )}
              {serve && (
                <div>
                  <div className={styles['detail-label']}>
                    {serve.label ?? 'Serve'}
                  </div>
                  <div className={styles['detail-info']}>{serve.total}</div>
                </div>
              )}
              {difficulty && (
                <div>
                  <div className={styles['detail-label']}>
                    {difficulty.label ?? 'Difficulty'}
                  </div>
                  <div className={styles['detail-info']}>
                    {Array.from({ length: 4 }, (_, index) => (
                      <span
                        key={title + index}
                        aria-hidden
                        className={clsx(styles['difficulty-dot'], {
                          [styles['difficulty-dot-filled']]:
                            index < difficulty.level,
                        })}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {source && (
            <div className={styles['source']}>
              {source.image && (
                <img
                  src={source.image}
                  alt={source.name}
                  className={styles['source-image']}
                />
              )}
              <div className={styles['source-name']}>{source.name}</div>
            </div>
          )}
        </div>
      </a>

      {recipeId && saveClickHandler && removeClickHandler && (
        <div className={styles['save']}>
          <FeatureRecipeSaveButton
            recipeId={recipeId}
            recipeTitle={title}
            variation="contrast"
            isSaved={isSaved}
            saveClickHandler={saveClickHandler}
            removeClickHandler={removeClickHandler}
          />
        </div>
      )}
    </div>
  );
};

export default FeatureRecipeTile;
